// ЕМИСС в интерфейсе: поиск по каталогу, менеджер показателя, проверка плана запроса.

import type { EmissCatalogItem, EmissCatalogSearch, EmissCatalogStatus, EmissInspection, EmissPlan, EmissStatus, SourceRequestLog, WidgetDefinition } from '../types';
import { api } from '../api';
import { formatDateTime, formatNumber as formatLocalizedNumber } from '../i18n';

import { controls } from './controls';
import { currentLocale } from './locale';
import { errorMessage, formatBytes, setStatus } from './status';
import { escapeHtml } from './dom';
import { loadIndicatorSections, populateIndicators, renderLeftEmissSelected } from './indicators';
import { state } from './state';
import { updateIndicatorControls } from './widget-form';

export function setEmissMessage(message: string, tone: 'neutral' | 'success' | 'warning' | 'error' = 'neutral'): void {
  controls.emissManagerMessage.textContent = message;
  controls.emissManagerMessage.dataset.tone = tone;
}

export function clearEmissInspectionState(clearLocalFields = true): void {
  state.emissInspection = null;
  controls.emissRegister.disabled = true;
  controls.emissInspectionSummary.innerHTML = '';
  controls.emissFilterList.innerHTML = '';
  if (clearLocalFields) {
    controls.emissLocalId.value = '';
    controls.emissName.value = '';
  }
}

export function emissCatalogStatusText(status: EmissCatalogItem['status']): string {
  return {
    actual: 'актуальный',
    excluded: 'скрыт/исключён',
    unknown: 'неизвестно',
  }[status];
}

export function emissCatalogStatusSummary(status: EmissCatalogStatus | null): string {
  if (!status || status.total_count === 0) {
    return 'Каталог ещё не загружен.';
  }
  const fetchedAt = status.fetched_at
    ? formatDateTime(status.fetched_at, currentLocale())
    : 'дата неизвестна';
  const stale = status.stale ? ' · требуется обновление' : '';
  return `${formatLocalizedNumber(status.actual_count, currentLocale(), 0)} актуальных показателей · всего ${formatLocalizedNumber(status.total_count, currentLocale(), 0)} · обновлено ${fetchedAt}${stale}`;
}

export function emissCatalogUserSummary(status: EmissCatalogStatus | null): string {
  if (!status || status.total_count === 0) {
    return 'Каталог ещё не загружен.';
  }
  const fetchedAt = status.fetched_at
    ? formatDateTime(status.fetched_at, currentLocale())
    : 'дата неизвестна';
  const stale = status.stale ? ' · можно обновить' : '';
  return `${formatLocalizedNumber(status.actual_count, currentLocale(), 0)} показателей · обновлено ${fetchedAt}${stale}`;
}

export function renderEmissCatalogStatus(status: EmissCatalogStatus | null): void {
  state.emissCatalogStatus = status;
  const text = emissCatalogStatusSummary(status);
  controls.emissCatalogStatus.textContent = text;
  controls.leftEmissCatalogStatus.textContent = emissCatalogUserSummary(status);
}

export function renderEmissCatalogResults(result: EmissCatalogSearch): void {
  state.emissCatalogResults = result.items;
  renderEmissCatalogStatus(result.catalog);
  if (!result.items.length) {
    controls.emissCatalogResults.innerHTML = '<p>Ничего не найдено.</p>';
    return;
  }
  const shown = result.items.length;
  controls.emissCatalogResults.innerHTML = `
    <div class="emiss-catalog-result-meta">Показано ${formatLocalizedNumber(shown, currentLocale(), 0)} из ${formatLocalizedNumber(result.total, currentLocale(), 0)}</div>
    ${result.items.map((item) => `
      <button class="emiss-catalog-result" type="button" data-emiss-catalog-select="${escapeHtml(item.indicator_id)}">
        <span class="emiss-catalog-result-title">${escapeHtml(item.title)}</span>
        <span class="emiss-catalog-result-details">
          <span>ID ${escapeHtml(item.indicator_id)}</span>
          <span class="emiss-catalog-badge status-${escapeHtml(item.status)}">${escapeHtml(emissCatalogStatusText(item.status))}</span>
          ${item.registered ? '<span class="emiss-catalog-badge registered">уже зарегистрирован</span>' : ''}
        </span>
      </button>
    `).join('')}
  `;
}

export function renderLeftEmissCatalogResults(result: EmissCatalogSearch): void {
  state.leftEmissCatalogResults = result.items;
  renderEmissCatalogStatus(result.catalog);
  if (!result.items.length) {
    controls.leftEmissCatalogResults.innerHTML = '<p>Ничего не найдено. Попробуйте другое название или ID.</p>';
    return;
  }
  controls.leftEmissCatalogResults.innerHTML = `
    <div class="emiss-catalog-result-meta">Показано ${formatLocalizedNumber(result.items.length, currentLocale(), 0)} из ${formatLocalizedNumber(result.total, currentLocale(), 0)}</div>
    ${result.items.map((item) => `
      <button class="emiss-catalog-result left-emiss-catalog-result" type="button" data-left-emiss-catalog-select="${escapeHtml(item.indicator_id)}" title="${escapeHtml(`Показатель ${item.indicator_id} · ${emissCatalogStatusText(item.status)}`)}">
        <span class="emiss-catalog-result-title">${escapeHtml(item.title)}</span>
        <span class="emiss-catalog-result-details">
          <span class="emiss-catalog-code">код ${escapeHtml(item.indicator_id)}</span>
          ${item.status !== 'actual' ? `<span class="emiss-catalog-badge status-${escapeHtml(item.status)}">${escapeHtml(emissCatalogStatusText(item.status))}</span>` : ''}
          ${item.registered ? '<span class="emiss-catalog-badge registered">уже подключён</span>' : ''}
          <span class="emiss-catalog-action">${item.registered ? 'Выбрать для виджета' : 'Исследовать и подключить'}</span>
        </span>
      </button>
    `).join('')}
  `;
}

export function setLeftEmissCatalogBusy(busy: boolean): void {
  controls.leftEmissCatalogSearch.disabled = busy;
  controls.leftEmissCatalogRefresh.disabled = busy;
}

export async function refreshEmissCatalog(showMessage = true): Promise<EmissCatalogStatus> {
  controls.emissCatalogRefresh.disabled = true;
  controls.emissCatalogSearch.disabled = true;
  if (showMessage) setEmissMessage('Обновляю каталог показателей fedstat.ru…');
  try {
    const result = await api.refreshEmissCatalog();
    renderEmissCatalogStatus(result);
    if (showMessage) {
      setEmissMessage(
        `Каталог обновлён: ${formatLocalizedNumber(result.refreshed_count, currentLocale(), 0)} показателей.`,
        'success',
      );
    }
    return result;
  } catch (error) {
    if (showMessage) setEmissMessage(`Каталог не обновлён: ${errorMessage(error)}`, 'error');
    throw error;
  } finally {
    controls.emissCatalogRefresh.disabled = false;
    controls.emissCatalogSearch.disabled = false;
  }
}

export async function refreshEmissCatalogStatus(): Promise<void> {
  try {
    renderEmissCatalogStatus(await api.emissCatalogStatus());
  } catch {
    state.emissCatalogStatus = null;
    controls.emissCatalogStatus.textContent = 'Статус каталога недоступен.';
    controls.leftEmissCatalogStatus.textContent = 'Статус каталога недоступен.';
  }
}

export async function ensureEmissCatalogForLeftSearch(): Promise<void> {
  if (state.emissCatalogStatus === null) {
    try {
      renderEmissCatalogStatus(await api.emissCatalogStatus());
    } catch (error) {
      throw error;
    }
  }
  if ((state.emissCatalogStatus?.total_count ?? 0) > 0) return;
  controls.leftEmissCatalogStatus.textContent = 'Каталог пуст, загружаю список показателей fedstat.ru...';
  try {
    await refreshEmissCatalog(false);
  } catch (error) {
    if ((state.emissCatalogStatus?.total_count ?? 0) === 0) throw error;
    setStatus(`Каталог не обновлён, поиск идёт по сохранённой версии: ${errorMessage(error)}`, 'normal');
  }
}

export async function refreshLeftEmissCatalog(): Promise<void> {
  setLeftEmissCatalogBusy(true);
  controls.leftEmissCatalogResults.innerHTML = '<p>Загружаю каталог показателей fedstat.ru...</p>';
  try {
    const result = await refreshEmissCatalog(false);
    state.leftEmissCatalogResults = [];
    controls.leftEmissCatalogResults.innerHTML = '<p>Каталог обновлён. Введите название или ID показателя для поиска.</p>';
    setStatus(`Каталог ЕМИСС обновлён: ${formatLocalizedNumber(result.total_count, currentLocale(), 0)} показателей`, 'success');
  } catch (error) {
    controls.leftEmissCatalogResults.innerHTML = `<p class="error-text">${escapeHtml(errorMessage(error))}</p>`;
    setStatus(`Каталог ЕМИСС не обновлён: ${errorMessage(error)}`, 'error');
  } finally {
    setLeftEmissCatalogBusy(false);
  }
}

export async function searchLeftEmissCatalog(): Promise<void> {
  const query = controls.indicatorSearch.value.trim() || controls.leftEmissCatalogQuery.value.trim();
  const includeExcluded = controls.leftEmissCatalogIncludeExcluded.checked;
  const sectionId = controls.indicatorSection.value;
  setLeftEmissCatalogBusy(true);
  controls.leftEmissCatalogResults.innerHTML = '<p>Ищу в каталоге fedstat.ru...</p>';
  try {
    await ensureEmissCatalogForLeftSearch();
    const result = await api.searchEmissCatalog(query, includeExcluded, 25, 0, currentLocale(), sectionId);
    renderLeftEmissCatalogResults(result);
    setStatus(
      result.total
        ? `В каталоге ЕМИСС найдено ${formatLocalizedNumber(result.total, currentLocale(), 0)} показателей`
        : 'В каталоге ЕМИСС ничего не найдено',
      result.total ? 'success' : 'normal',
    );
  } catch (error) {
    controls.leftEmissCatalogResults.innerHTML = `<p class="error-text">${escapeHtml(errorMessage(error))}</p>`;
    setStatus(`Поиск в каталоге ЕМИСС не выполнен: ${errorMessage(error)}`, 'error');
  } finally {
    setLeftEmissCatalogBusy(false);
  }
}

export async function searchEmissCatalog(): Promise<void> {
  const query = controls.emissCatalogQuery.value.trim();
  const includeExcluded = controls.emissCatalogIncludeExcluded.checked;
  controls.emissCatalogSearch.disabled = true;
  try {
    if (state.emissCatalogStatus === null) {
      renderEmissCatalogStatus(await api.emissCatalogStatus());
    }
    if ((state.emissCatalogStatus?.total_count ?? 0) === 0) {
      setEmissMessage('Каталог пуст, сначала загружаю список показателей…');
      await refreshEmissCatalog(false);
    }
    const result = await api.searchEmissCatalog(query, includeExcluded, 25, 0, currentLocale());
    renderEmissCatalogResults(result);
    setEmissMessage(
      result.total
        ? `Найдено ${formatLocalizedNumber(result.total, currentLocale(), 0)} показателей. Выберите строку и затем исследуйте показатель.`
        : 'По этому запросу ничего не найдено.',
      result.total ? 'success' : 'warning',
    );
  } catch (error) {
    controls.emissCatalogResults.innerHTML = `<p class="error-text">${escapeHtml(errorMessage(error))}</p>`;
    setEmissMessage(`Поиск в каталоге не выполнен: ${errorMessage(error)}`, 'error');
  } finally {
    controls.emissCatalogSearch.disabled = false;
  }
}

export function selectEmissCatalogItem(item: EmissCatalogItem): void {
  controls.emissIndicatorId.value = item.indicator_id;
  clearEmissInspectionState(true);
  controls.emissName.value = item.title;
  setEmissMessage(`Показатель ${item.indicator_id} выбран из каталога. Запустите исследование показателя.`, 'success');
  controls.emissInspect.focus();
}

export async function selectLeftEmissCatalogItem(item: EmissCatalogItem): Promise<void> {
  controls.source.value = 'emiss';
  await loadIndicatorSections();
  populateIndicators();
  if (item.registered && item.local_indicator_id) {
    if (!state.indicators.some((indicator) => indicator.id === item.local_indicator_id)) {
      state.indicators = await api.indicators(true, currentLocale());
      populateIndicators();
    }
    controls.indicator.value = item.local_indicator_id;
    renderLeftEmissSelected();
    await updateIndicatorControls();
    setStatus(`Подключённый показатель ЕМИСС выбран: ${item.title}`, 'success');
    return;
  }
  controls.indicator.value = '';
  renderLeftEmissSelected();
  controls.emissIndicatorId.value = item.indicator_id;
  clearEmissInspectionState(true);
  controls.emissName.value = item.title;
  await openEmissManager();
  setEmissMessage(`Показатель ${item.indicator_id} выбран из каталога. Исследуйте и подключите его, чтобы использовать в виджетах.`, 'success');
  controls.emissInspect.focus();
  setStatus('Показатель ЕМИСС открыт в режиме диагностики и подключения', 'normal');
}

export function renderEmissConnection(status: EmissStatus): void {
  state.emissStatus = status;
  controls.emissConnectionBadge.textContent = status.enabled ? 'Коннектор включён' : 'Коннектор отключён';
  controls.emissConnectionBadge.dataset.state = status.enabled ? 'ok' : 'disabled';
  const route = status.transport === 'explicit-proxy'
    ? 'через явно настроенный прокси'
    : status.transport === 'environment-proxy'
      ? 'через прокси окружения'
      : 'напрямую с сервера';
  const latest = status.last_request
    ? ` Последнее обращение: ${status.last_request.status}${status.last_request.http_status ? `, HTTP ${status.last_request.http_status}` : ''}.`
    : '';
  controls.emissConnectionDescription.textContent = `${route}; TLS-проверка ${status.tls_verification ? 'включена' : 'отключена'}; предел ответа ${formatBytes(status.limits.data_bytes)}; оценочный предел ${formatLocalizedNumber(status.limits.estimated_cells, currentLocale(), 0)} ячеек.${latest}`;
  controls.emissDiagnostics.disabled = !status.enabled || status.diagnostics_enabled === false;
  controls.emissInspect.disabled = !status.enabled;
  if (!status.enabled) {
    setEmissMessage('Коннектор отключён конфигурацией backend. Для российского сервера установите ENABLE_EMISS=true; для разработки также можно задать EMISS_PROXY_URL.', 'warning');
  }
}

export function emissRoleLabel(role: string): string {
  return {
    indicator: 'показатель',
    territory: 'территория',
    year: 'год',
    month: 'месяц',
    dimension: 'селектор',
    fixed: 'фиксированный фильтр',
    unresolved: 'требует настройки',
  }[role] ?? role;
}

export function renderEmissInspection(inspection: EmissInspection): void {
  state.emissInspection = inspection;
  controls.emissRegister.disabled = false;
  if (!controls.emissLocalId.value.trim()) controls.emissLocalId.value = `emiss_${inspection.indicator_id}`;
  if (!controls.emissName.value.trim()) controls.emissName.value = inspection.indicator_title;

  const years = inspection.available_years.length
    ? `${inspection.available_years[0]}–${inspection.available_years.at(-1)} (${inspection.available_years.length})`
    : 'не определены';
  const safety = inspection.safe_to_activate
    ? '<span class="emiss-safety ok">Можно активировать</span>'
    : '<span class="emiss-safety warning">Требуется настройка фильтров</span>';
  controls.emissInspectionSummary.innerHTML = `
    <div class="emiss-summary-title"><strong>${escapeHtml(inspection.indicator_title)}</strong>${safety}</div>
    <dl class="emiss-summary-grid">
      <div><dt>ID</dt><dd>${escapeHtml(inspection.indicator_id)}</dd></div>
      <div><dt>Периодичность</dt><dd>${inspection.frequency === 'monthly' ? 'месячная' : 'годовая'}</dd></div>
      <div><dt>Годы</dt><dd>${escapeHtml(years)}</dd></div>
      <div><dt>Фильтров</dt><dd>${inspection.filters.length}</dd></div>
    </dl>
    <fieldset class="emiss-field-mapping">
      <legend>Семантические роли полей</legend>
      ${(['territory', 'year', 'month'] as const).map((role) => {
        const labels = { territory: 'Территория', year: 'Год', month: 'Месяц' };
        const optional = role === 'month';
        return `<label class="field"><span>${labels[role]}</span><select data-emiss-role-map="${role}">
          ${optional ? '<option value="">Не используется</option>' : '<option value="">Не определено</option>'}
          ${inspection.filters.filter((item) => item.role !== 'indicator').map((item) => `<option value="${escapeHtml(item.field_id)}" ${inspection.field_map[role] === item.field_id ? 'selected' : ''}>${escapeHtml(item.title)} · ${escapeHtml(item.field_id)}</option>`).join('')}
        </select></label>`;
      }).join('')}
      <small>Проверьте автоматически определённые роли. Ошибочная роль территории или периода приведёт к неверному срезу.</small>
    </fieldset>
    ${inspection.warnings.length ? `<div class="emiss-warning-list">${inspection.warnings.map((warning) => `<p>⚠ ${escapeHtml(warning)}</p>`).join('')}</div>` : ''}
  `;

  controls.emissFilterList.innerHTML = inspection.filters.map((filter) => {
    const preview = filter.values.slice(0, 8).map((value) => escapeHtml(value.title)).join(', ');
    const needsFixedChoice = filter.role === 'unresolved';
    const isDimension = filter.role === 'dimension';
    const choices = filter.values
      .map((value) => `<option value="${escapeHtml(value.id)}" ${!filter.requires_default_confirmation && filter.default_value_ids.includes(value.id) ? 'selected' : ''}>${escapeHtml(value.title)} · ${escapeHtml(value.id)}</option>`)
      .join('');
    const dimensionPrompt = filter.requires_default_confirmation
      ? '<strong>Требуется подтверждение:</strong> источник не содержит однозначного итога.'
      : 'Значение используется при первом добавлении виджета и остаётся доступным как селектор.';
    return `
      <article class="emiss-filter-card" data-emiss-filter-card="${escapeHtml(filter.field_id)}">
        <div class="emiss-filter-card-heading">
          <div><strong>${escapeHtml(filter.title)}</strong><small>поле ${escapeHtml(filter.field_id)} · ${formatLocalizedNumber(filter.value_count, currentLocale(), 0)} значений</small></div>
          <span class="emiss-role role-${escapeHtml(filter.role)}">${escapeHtml(emissRoleLabel(filter.role))}</span>
        </div>
        <p class="emiss-filter-preview">${preview || 'Значения отсутствуют'}${filter.values_truncated ? '…' : ''}</p>
        ${isDimension ? `
          <div class="emiss-filter-resolution ${filter.requires_default_confirmation ? 'requires-confirmation' : ''}">
            <label class="field full-width"><span>Значение по умолчанию</span><select data-emiss-default-select multiple size="4">${choices}</select></label>
            <label class="field full-width"><span>Дополнительные ID через запятую</span><input data-emiss-default-ids placeholder="Например: 1, 2" /></label>
            <small>${dimensionPrompt}</small>
          </div>` : ''}
        ${needsFixedChoice ? `
          <div class="emiss-filter-resolution">
            <label class="field full-width"><span>Найти значение</span><div class="emiss-inline-search"><input data-emiss-filter-search-input placeholder="Название или ID" /><button class="button button-secondary" data-emiss-filter-search type="button">Найти</button></div></label>
            <label class="field full-width"><span>Зафиксировать значения</span><select data-emiss-fixed-select multiple size="4">${choices}</select></label>
            <label class="field full-width"><span>Либо введите ID через запятую</span><input data-emiss-fixed-ids placeholder="Например: 1, 2" /></label>
          </div>` : ''}
      </article>`;
  }).join('');

  const unresolved = inspection.filters.filter((item) => item.role === 'unresolved').length;
  const confirmations = inspection.filters.filter((item) => item.requires_default_confirmation).length;
  const pending = unresolved + confirmations;
  setEmissMessage(
    pending
      ? `Метаданные получены. Требуют решения: неопределённых фильтров — ${unresolved}, неподтверждённых defaults — ${confirmations}. Без этого показатель будет сохранён неактивным.`
      : 'Метаданные получены и проверены. Показатель готов к регистрации.',
    pending ? 'warning' : 'success',
  );
}

export function collectEmissFieldMap(): Record<string, string> {
  const result: Record<string, string> = {};
  controls.emissInspectionSummary.querySelectorAll<HTMLSelectElement>('[data-emiss-role-map]').forEach((select) => {
    const role = select.dataset.emissRoleMap;
    if (role && select.value) result[role] = select.value;
  });
  return result;
}

export function collectEmissFixedFilters(): Record<string, string[]> {
  const inspection = state.emissInspection;
  const result: Record<string, string[]> = inspection
    ? Object.fromEntries(Object.entries(inspection.fixed_filters).map(([key, values]) => [key, [...values]]))
    : {};
  controls.emissFilterList.querySelectorAll<HTMLElement>('[data-emiss-filter-card]').forEach((card) => {
    const fieldId = card.dataset.emissFilterCard;
    if (!fieldId) return;
    const selected = Array.from(card.querySelectorAll<HTMLSelectElement>('[data-emiss-fixed-select] option:checked'))
      .map((option) => option.value);
    const manual = (card.querySelector<HTMLInputElement>('[data-emiss-fixed-ids]')?.value ?? '')
      .split(/[,;\n]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    const values = [...new Set([...selected, ...manual])];
    if (values.length) result[fieldId] = values;
  });
  return result;
}

export function collectEmissDimensionDefaults(): Record<string, string[]> {
  const inspection = state.emissInspection;
  if (!inspection) return {};
  const fieldToDimension = Object.fromEntries(
    Object.entries(inspection.dimension_field_map).map(([dimensionId, fieldId]) => [fieldId, dimensionId]),
  );
  const result: Record<string, string[]> = {};
  controls.emissFilterList.querySelectorAll<HTMLElement>('[data-emiss-filter-card]').forEach((card) => {
    const fieldId = card.dataset.emissFilterCard;
    const dimensionId = fieldId ? fieldToDimension[fieldId] : undefined;
    if (!dimensionId) return;
    const selected = Array.from(card.querySelectorAll<HTMLSelectElement>('[data-emiss-default-select] option:checked'))
      .map((option) => option.value);
    const manual = (card.querySelector<HTMLInputElement>('[data-emiss-default-ids]')?.value ?? '')
      .split(/[,;\n]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    const values = [...new Set([...selected, ...manual])];
    if (values.length) result[dimensionId] = values;
  });
  return result;
}

export function renderEmissRequestLog(logs: SourceRequestLog[]): void {
  if (!logs.length) {
    controls.emissRequestLog.innerHTML = '<p>Обращений к источнику пока нет.</p>';
    return;
  }
  controls.emissRequestLog.innerHTML = logs.map((item) => {
    const status = item.status === 'success' ? 'ok' : 'error';
    const when = formatDateTime(item.created_at, currentLocale());
    const detail = item.status === 'success'
      ? `${item.http_status ?? '—'} · ${formatBytes(item.response_bytes)} · ${item.elapsed_ms?.toFixed(0) ?? '—'} мс`
      : `${item.error_category ?? 'ошибка'}${item.http_status ? ` · HTTP ${item.http_status}` : ''}`;
    return `<article class="emiss-log-entry">
      <span class="emiss-log-dot ${status}" aria-hidden="true"></span>
      <div><strong>${escapeHtml(item.operation)}</strong><small>${escapeHtml(item.indicator_id ?? '—')} · ${escapeHtml(when)}</small><span>${escapeHtml(detail)} · попыток: ${item.attempts}</span></div>
    </article>`;
  }).join('');
}

export async function refreshEmissRequestLog(): Promise<void> {
  controls.refreshEmissLog.disabled = true;
  try {
    renderEmissRequestLog(await api.emissRequestLogs(30));
  } catch (error) {
    controls.emissRequestLog.innerHTML = `<p class="error-text">${escapeHtml(errorMessage(error))}</p>`;
  } finally {
    controls.refreshEmissLog.disabled = false;
  }
}

export async function openEmissManager(): Promise<void> {
  if (!controls.emissDialog.open) controls.emissDialog.showModal();
  controls.openEmissManager.setAttribute('aria-expanded', 'true');
  try {
    renderEmissConnection(await api.emissStatus());
  } catch (error) {
    controls.emissConnectionBadge.textContent = 'Backend недоступен';
    controls.emissConnectionBadge.dataset.state = 'error';
    controls.emissConnectionDescription.textContent = errorMessage(error);
  }
  await refreshEmissCatalogStatus();
  await refreshEmissRequestLog();
}

export function closeEmissManager(): void {
  if (controls.emissDialog.open) controls.emissDialog.close();
  controls.openEmissManager.setAttribute('aria-expanded', 'false');
}

export async function diagnoseEmiss(): Promise<void> {
  const indicatorId = controls.emissIndicatorId.value.trim();
  if (!/^\d{1,12}$/.test(indicatorId)) {
    setEmissMessage('ID показателя должен состоять из 1–12 цифр.', 'error');
    return;
  }
  controls.emissDiagnostics.disabled = true;
  setEmissMessage('Проверяется доступ к странице показателя ЕМИСС…');
  try {
    const result = await api.emissDiagnostics(indicatorId, true);
    setEmissMessage(
      result.ok
        ? `Доступ подтверждён: «${result.title ?? indicatorId}», фильтров ${result.filter_count ?? 0}, ${result.elapsed_ms.toFixed(0)} мс.`
        : `Доступ не подтверждён: ${result.error_message ?? result.error_category ?? 'неизвестная ошибка'}.`,
      result.ok ? 'success' : 'error',
    );
  } catch (error) {
    setEmissMessage(`Диагностика не выполнена: ${errorMessage(error)}`, 'error');
  } finally {
    controls.emissDiagnostics.disabled = !(state.emissStatus?.enabled ?? false);
    await refreshEmissRequestLog();
  }
}

export async function inspectEmissIndicator(): Promise<void> {
  const indicatorId = controls.emissIndicatorId.value.trim();
  if (!/^\d{1,12}$/.test(indicatorId)) {
    setEmissMessage('ID показателя должен состоять из 1–12 цифр.', 'error');
    return;
  }
  controls.emissInspect.disabled = true;
  controls.emissRegister.disabled = true;
  setEmissMessage('Загружаются и анализируются метаданные показателя…');
  try {
    const inspection = await api.inspectEmiss(indicatorId, {
      forceRefresh: controls.emissForceRefresh.checked,
    });
    renderEmissInspection(inspection);
  } catch (error) {
    state.emissInspection = null;
    controls.emissInspectionSummary.innerHTML = '';
    controls.emissFilterList.innerHTML = '';
    setEmissMessage(`Не удалось исследовать показатель: ${errorMessage(error)}`, 'error');
  } finally {
    controls.emissInspect.disabled = !(state.emissStatus?.enabled ?? false);
    await refreshEmissRequestLog();
  }
}

export async function searchEmissFilterValues(card: HTMLElement): Promise<void> {
  const inspection = state.emissInspection;
  const fieldId = card.dataset.emissFilterCard;
  if (!inspection || !fieldId) return;
  const input = card.querySelector<HTMLInputElement>('[data-emiss-filter-search-input]');
  const select = card.querySelector<HTMLSelectElement>('[data-emiss-fixed-select]');
  const button = card.querySelector<HTMLButtonElement>('[data-emiss-filter-search]');
  if (!select || !button) return;
  button.disabled = true;
  button.textContent = 'Поиск…';
  try {
    const result = await api.emissFilterValues(inspection.indicator_id, fieldId, input?.value.trim() ?? '', 200);
    select.innerHTML = result.values
      .map((value) => `<option value="${escapeHtml(value.id)}">${escapeHtml(value.title)} · ${escapeHtml(value.id)}</option>`)
      .join('');
    if (!result.values.length) setEmissMessage('Для этого фильтра ничего не найдено.', 'warning');
    else setEmissMessage(`Найдено ${formatLocalizedNumber(result.total, currentLocale(), 0)} значений; показано ${formatLocalizedNumber(result.values.length, currentLocale(), 0)}.`, 'success');
  } catch (error) {
    setEmissMessage(`Поиск значений не выполнен: ${errorMessage(error)}`, 'error');
  } finally {
    button.disabled = false;
    button.textContent = 'Найти';
  }
}

export async function registerEmissIndicator(): Promise<void> {
  const inspection = state.emissInspection;
  if (!inspection) {
    setEmissMessage('Сначала исследуйте показатель.', 'warning');
    return;
  }
  const fixedFilters = collectEmissFixedFilters();
  const dimensionDefaults = collectEmissDimensionDefaults();
  const unresolvedWithoutValue = inspection.filters.filter((filter) =>
    filter.role === 'unresolved' && !(fixedFilters[filter.field_id]?.length),
  );
  const unconfirmedDefaults = inspection.filters.filter((filter) => {
    if (!filter.requires_default_confirmation) return false;
    const dimensionId = Object.entries(inspection.dimension_field_map)
      .find(([, fieldId]) => fieldId === filter.field_id)?.[0];
    return !dimensionId || !(dimensionDefaults[dimensionId]?.length);
  });
  if (controls.emissActive.checked && (unresolvedWithoutValue.length || unconfirmedDefaults.length)) {
    const names = [...unresolvedWithoutValue, ...unconfirmedDefaults].map((item) => item.title);
    setEmissMessage(`Для активации подтвердите значения фильтров: ${names.join(', ')}.`, 'warning');
    return;
  }
  controls.emissRegister.disabled = true;
  setEmissMessage('Показатель регистрируется в локальном каталоге…');
  try {
    const result = await api.registerEmiss({
      indicator_id: inspection.indicator_id,
      local_id: controls.emissLocalId.value.trim() || null,
      name: controls.emissName.value.trim() || null,
      unit: controls.emissUnit.value.trim() || 'ед.',
      field_map: collectEmissFieldMap(),
      fixed_filters: fixedFilters,
      dimension_defaults: dimensionDefaults,
      cache_ttl_seconds: 86400,
      active: controls.emissActive.checked,
      overwrite: controls.emissOverwrite.checked,
      force_refresh: controls.emissForceRefresh.checked,
    });
    state.emissInspection = result.inspection;
    const activeMessage = result.indicator.active
      ? 'Показатель активирован и добавлен в селектор.'
      : 'Показатель сохранён неактивным: необходимо разрешить неопределённые фильтры.';
    setEmissMessage(`${activeMessage}${result.warnings.length ? ` ${result.warnings.join(' ')}` : ''}`, result.indicator.active ? 'success' : 'warning');
    const [indicators, territories] = await Promise.all([
      api.indicators(true, currentLocale()),
      api.territories({}, currentLocale()),
    ]);
    state.indicators = indicators;
    state.localTerritories = territories;
    state.territories = [...territories];
    state.catalogTerritories = [...territories];
    await loadIndicatorSections();
    populateIndicators();
    if (result.indicator.active) {
      controls.indicator.value = result.indicator.id;
      renderLeftEmissSelected();
      await updateIndicatorControls();
      setStatus(`Показатель ЕМИСС «${result.indicator.name}» зарегистрирован`, 'success');
      if (controls.indicatorSearch.value.trim() || controls.leftEmissCatalogQuery.value.trim() || state.leftEmissCatalogResults.length) {
        void searchLeftEmissCatalog();
      }
    }
  } catch (error) {
    setEmissMessage(`Регистрация не выполнена: ${errorMessage(error)}`, 'error');
  } finally {
    controls.emissRegister.disabled = false;
    await refreshEmissRequestLog();
  }
}

export function emissPlanProblem(plan: EmissPlan): string | null {
  if (plan.unresolved_filters.length) {
    const details = plan.unresolved_filters
      .slice(0, 4)
      .map((filter) => `${filter.title}: ${filter.reason}`)
      .join('; ');
    const extra = plan.unresolved_filters.length > 4
      ? `; ещё ${plan.unresolved_filters.length - 4}`
      : '';
    return `ЕМИСС не готов к запросу: ${details}${extra}`;
  }
  if (!plan.within_limits) {
    return `Запрос ЕМИСС слишком большой: ${formatLocalizedNumber(plan.estimated_cells, currentLocale(), 0)} ячеек, ${formatLocalizedNumber(plan.total_selected_values, currentLocale(), 0)} значений фильтров. Уменьшите выборку.`;
  }
  return null;
}

export async function validateEmissWidgetPlan(widget: WidgetDefinition): Promise<void> {
  const indicator = state.indicators.find((item) => item.id === widget.query.indicatorId);
  if (indicator?.source_type !== 'emiss') return;
  const plan = await api.planEmissQuery(indicator.id, widget.query);
  const problem = emissPlanProblem(plan);
  if (problem) throw new Error(problem);
}
